import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    IconButton,
    Avatar,
    Button,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { ButtonStyles } from "../../Styles/ButtonStyles";
import CustomTable from "../../ReusableComponents/Table/CustomTable";
import AddBanner from "./AddBanner";
import CustomSnackbar from "../../components/CustomSnackbar";
import api from "../../api/api";
import en_ja from "../../translations/en_ja";

const BannerManagement = () => {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarType, setSnackbarType] = useState("success");
    const [loading, setLoading] = useState(true);
    const [bannerData, setBannerData] = useState([]);
    const [editMode, setEditMode] = useState(false);
    const [selectedBanner, setSelectedBanner] = useState(null);

    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [bannerToDelete, setBannerToDelete] = useState(null);

    const getAllBannerData = async () => {
        setLoading(true);
        try {
            const res = await api.get("/v1/admin/get/all-banners");
            if (res?.data?.data) {
                setBannerData(res.data.data);
            }
        } catch (err) {
            console.error("Error fetching banners:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getAllBannerData();
    }, []);

    const handleAddBanner = () => {
        setEditMode(false);
        setSelectedBanner(null);
        setDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (bannerToDelete) {
            try {
                await api.delete(`/v1/admin/delete/banner/${bannerToDelete.id}`);
                setDeleteConfirmOpen(false);
                setBannerToDelete(null);
                setSnackbarMessage(en_ja["Banner deleted successfully"]);
                setSnackbarType("success");
                setSnackbarOpen(true);
                getAllBannerData();
            } catch (error) {
                console.error("Error deleting banner:", error);
                setSnackbarMessage(en_ja["Failed to delete banner"]);
                setSnackbarType("error");
                setSnackbarOpen(true);
            }
        }
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
        setSelectedBanner(null);
        setEditMode(false);
    };

    const handleDialogSubmit = () => {
        getAllBannerData();
    };

    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
        setSnackbarMessage("");
        setSnackbarType("success");
    };

    const handleEdit = (row) => {
        setSelectedBanner(row);
        setEditMode(true);
        setDialogOpen(true);
    };

    const handleDeleteClick = (row) => {
        setBannerToDelete(row);
        setDeleteConfirmOpen(true);
    };

    const handleDeleteCancel = () => {
        setDeleteConfirmOpen(false);
        setBannerToDelete(null);
    };

    const columns = [
        {
            id: "banner_title",
            label: "Banner Name",
            render: (row) => (
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Avatar
                        variant="rounded"
                        src={row.banner_image}
                        sx={{ width: 120, height: 60, borderRadius: "8px" }}
                    />
                    <Box>
                        <Typography sx={{ fontWeight: 600, fontSize: "14px", lineHeight: "20px" }}>
                            {row.banner_title}
                        </Typography>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontSize: "13px", lineHeight: "18px" }}
                        >
                            {row.description}
                        </Typography>
                    </Box>
                </Box>
            ),
        },
        {
            id: "created_at",
            label: "Created",
            sortable: true,
            render: (row) => {
                if (!row.created_at) return "-";
                const date = new Date(row.created_at);
                if (isNaN(date)) return "-";
                return `${String(date.getDate()).padStart(2, "0")}/${String(
                    date.getMonth() + 1
                ).padStart(2, "0")}/${date.getFullYear()}`;
            },
        },
        {
            id: "actions",
            label: en_ja["Actions"],
            render: (row) => (
                <Box display="flex" justifyContent="start" gap={1}>
                    <Tooltip title={en_ja["Edit Banner"]} arrow>
                        <IconButton aria-label="edit" size="small" onClick={() => handleEdit(row)}>
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={en_ja["Delete Banner"]} arrow>
                        <IconButton
                            aria-label="delete"
                            size="small"
                            onClick={() => handleDeleteClick(row)}
                            sx={{
                                color: "error.main",
                                "&:hover": { backgroundColor: "error.light", color: "error.dark" },
                            }}
                        >
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            ),
        },
    ];

    return (
        <Box sx={{ mt: 2 }}>
            <CustomSnackbar
                isOpen={snackbarOpen}
                type={snackbarType}
                message={snackbarMessage}
                onClose={handleSnackbarClose}
            />

            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 3 }}>
                <Button sx={ButtonStyles} onClick={handleAddBanner}>
                    {en_ja["Add New Banner"]}
                </Button>
            </Box>

            <CustomTable
                columns={columns}
                rows={bannerData}
                showIndexColumn={true}
                loading={loading}
            />

            <AddBanner
                dialogOpen={dialogOpen}
                onClose={handleDialogClose}
                editMode={editMode}
                bannerData={selectedBanner}
                onSubmit={handleDialogSubmit}
                setSnackbarType={setSnackbarType}
                setSnackbarOpen={setSnackbarOpen}
                setSnackbarMessage={setSnackbarMessage}
            />

            {/* ✅ Fixed Delete Confirmation Dialog */}
            <Dialog
                open={deleteConfirmOpen}
                onClose={handleDeleteCancel}
                aria-labelledby="delete-dialog-title"
                aria-describedby="delete-dialog-description"
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle id="delete-dialog-title" sx={{ color: "error.main" }}>
                    {en_ja["Confirm Delete"]}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="delete-dialog-description">
                        {en_ja["Are you sure you want to delete the banner"]}{" "}
                        <strong>{bannerToDelete?.banner_title}</strong>?{" "}
                        {en_ja["This action cannot be undone."]}
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: 2, gap: 1 }}>
                    <Button onClick={handleDeleteCancel} variant="outlined" color="primary">
                        {en_ja["Cancel"]}
                    </Button>
                    <Button onClick={handleDeleteConfirm} variant="contained" color="error" autoFocus>
                        {en_ja["Delete"]}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default BannerManagement;
